import { useMemo } from 'react';

import gql from 'graphql-tag';

import { useSanityQuery } from 'state/graphql';
import { useUIContext } from 'state/ui';

// featurePartnerLinking is a singleton in the CMS (see th-app-cms
// desk-structure). Query it directly by its fixed documentId so these
// microsite screens don't need the app-shell FeaturesContext.
const PARTNER_LINKING_SINGLETON_ID = 'feature-partner-linking-singleton';

// Illustration fields on the featurePartnerLinking singleton. Each error
// screen fetches only its own field so we never over-fetch.
export type PartnerLinkingIllustration =
  | 'rateLimitedIllustration'
  | 'errorIllustration'
  | 'regionUnavailableIllustration';

interface ILocaleImage {
  locale?: { asset?: { _id?: string | null } | null } | null;
}

interface IFeaturePartnerLinkingData {
  FeaturePartnerLinking?:
    | ({ _id: string } & Partial<Record<PartnerLinkingIllustration, ILocaleImage | null>>)
    | null;
}

export interface IPartnerLinkingImage {
  imageUrl: string;
  loading: boolean;
}

/**
 * Fetches a single partner-linking illustration from the Sanity singleton and
 * returns a CDN url built with the shared image-url builder (same region-aware
 * pipeline as the rest of the site). `imageUrl` is an empty string until the
 * document resolves or when the field is unset, so callers can fall back to a
 * placeholder.
 */
export const usePartnerLinkingImage = (
  illustration: PartnerLinkingIllustration
): IPartnerLinkingImage => {
  const { buildImageUrl } = useUIContext();

  // Select only the requested field. `illustration` is a compile-time union
  // (never user input), so interpolating it into the query is safe.
  const query = useMemo(
    () => gql`
      query FeaturePartnerLinking_${illustration}($id: ID!) {
        FeaturePartnerLinking(id: $id) {
          _id
          ${illustration} {
            locale: en {
              asset {
                _id
              }
            }
          }
        }
      }
    `,
    [illustration]
  );

  const { data, loading } = useSanityQuery<IFeaturePartnerLinkingData>(query, {
    variables: { id: PARTNER_LINKING_SINGLETON_ID },
  });

  const assetId = data?.FeaturePartnerLinking?.[illustration]?.locale?.asset?._id;

  return {
    imageUrl: assetId ? buildImageUrl(assetId) : '',
    loading,
  };
};
